<script>
import {computed, defineComponent, onBeforeMount, reactive, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useApiCall} from "@/composables/useApiCall";
import {
  receiptApiCall,
  receiptUploadApiCall,
  receiptDeleteApiCall,
} from "@/services/receipt";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Paginator from "primevue/paginator";
import MainCard from "@/components/cards/MainCard";
import ButtonFileUpload from "@/components/buttons/ButtonFileUpload.vue";
import Button from "primevue/button";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import Calendar from "primevue/calendar";

export default defineComponent({
  layout: {name: 'AdminLayout'},
  components: {
    Calendar,
    ConfirmationModal,
    ButtonFileUpload,
    DataTable,
    Column,
    Paginator,
    MainCard,
    Button,
  },
  setup() {
    const {t} = useI18n();
    const errors = ref([])
    const first = ref(0)
    const currentType = ref(null)
    const form = reactive({
      fields: {
        meters: {
          month: '',
          year: '',
          file: null,
        },
        balance: {
          month: '',
          year: '',
          file: null,
        },
        receipt: {
          month: '',
          year: '',
          file: null,
        },
      }
    })

    const {
      data: contentData,
      isLoading: contentLoading,
      error: contentError,
      executeApiCall: getContentAction,
    } = useApiCall(receiptApiCall, true)

    const {
      isLoading: isContentUploadLoading,
      data: contentUploadData,
      error: contentUploadError,
      executeApiCall: uploadContentAction,
    } = useApiCall(receiptUploadApiCall, true)

    const {
      isLoading: isDeleteLoading,
      data: deleteData,
      error: deleteDataError,
      executeApiCall: deleteAction,
    } = useApiCall(receiptDeleteApiCall, true)

    const destroyFileMemory = (key) => {
      deleteError(key)
      mainError.value = ''
      form.fields[key] = null
    }

    const loadFileMemory = (data, index) => {
      switch (index) {
        case 0:
          form.fields.meters.file = data.files[0]
          break
        case 1:
          form.fields.balance.file = data.files[0]
          break
        default:
          form.fields.receipt.file = data.files[0]
          break
      }
    }

    watch((first), async (index) => {
      await changePage();
    });

    const changePage = async () => {
      await getContentAction({
        page: ((first.value / (contentData.value?.data?.pagination?.per_page ?? 1)) + 1),
      })
      window.scrollTo(0, 0);
    }
    const mainError = ref('')
    const uploadFile = async (type) => {
      deleteError(type)
      currentType.value = type
      mainError.value = ''
      try {
        const formData = new FormData()
        formData.append('file', form.fields[type].file)
        if (type === 'receipt' || type === 'balance') {
          formData.append('month', form.fields[type].month.getMonth())
          formData.append('year', form.fields[type].year.getFullYear())
        }
        await uploadContentAction({data: formData, type})
        if (contentUploadData.value) {
          form.fields[type].file = null
          form.fields[type].month = ''
          form.fields[type].year = ''
          first.value = 0
          currentType.value = null
          await getContentAction()
        }
      } catch (e) {
        if (contentUploadError.value && contentUploadError.value.status === 500) {
          errors.value.push({ value: { file: ['Ошибка'] }, key: type })
        } else if (contentUploadError.value.data) {
          errors.value.push({ value: contentUploadError.value.data, key: type })
        } else {
          mainError.value = contentUploadError.value.error
        }
      }
    }

    const deleteError = (type) => {
      errors.value = [...errors.value.filter(item => item.key !== type)]
    }

    const getError = (type) => {
      const error = errors.value.find(item => item.key === type)
      if (error) {
        return error.value.file[0]
      } else {
        return null
      }
    }

    const getDataType = (type) => {
      switch (type) {
        case 'meters':
          return t('receipt.type.meters')
        case 'balance':
          return t('receipt.type.balance')
        default:
          return t('receipt.type.receipt')
      }
    }

    const getFileName = (file) => {
      return file.split('/').pop()
    }
    const deleteItem = ref(null)
    const visible = ref(false)
    const openDeleteModal = (data = null) => {
      deleteItem.value = data
      visible.value = !visible.value
    }

    const deleteDocument = async () => {
      try {
        await deleteAction({id: deleteItem.value.id})
        if (deleteData.value) {
          openDeleteModal()
          await getContentHandler()
        }
      } catch (e) {
        console.error(e)
      }
    }

    const getContentHandler = async () => {
      try {
        await getContentAction()
      } catch (e) {
        errors.value = e
        console.error(e)
      }
    }

    onBeforeMount(async () => {
      await getContentHandler()
    });

    return {
      t,
      form,
      first,
      destroyFileMemory,
      loadFileMemory,
      uploadFile,
      getDataType,
      getFileName,
      contentLoading,
      isContentUploadLoading,
      errors,
      contentData,
      currentType,
      deleteItem,
      visible,
      isDeleteLoading,
      getError,
      deleteDocument,
      openDeleteModal,
      mainError,
    };
  }
});
</script>

<template>
  <ConfirmationModal :visible="visible">
    <template #header>
      <span class="delete__title">Удалить {{ getFileName(deleteItem.file) }}</span>
    </template>

    <template #default>
      <span>Вы уверены,  что хотите удалить файл? Это действие будет невозможно отменить</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between">
        <Button :label="t('labels.cancel')" @click="openDeleteModal" class="btn-primary-outlined font-light w-12"/>
        <Button :label="t('labels.destroy')" :loading="isDeleteLoading" @click="deleteDocument" class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <section class="py-2 mb-3 receipt ">
    <span class="receipt__title flex mb-4">{{ t('receipt.addFile') }}</span>
    <div class="receipt__upload">
      <MainCard v-for="(value, key, i) in form.fields" :title="t(`receipt.type.${key}`)">
        <div class="flex flex-column gap-4 mt-2">
          <div v-if="key === 'receipt' || key === 'balance'" class="receipt__upload-calendar">
            <span class="p-float-label w-full">
              <span class="p-float-label relative">
                <Calendar
                    v-model="form.fields[key].month"
                    date-format="M"
                    placeholder="Месяц"
                    view="month"
                    :yearNavigator="false"
                    panelClass="no-year"
                    class="w-full no-year" />
                <label for="active_until">Месяц</label>
                <i class="pi pi-calendar calendar-icon" />
              </span>
            </span>
            <span class="p-float-label w-full">
              <span class="p-float-label relative">
                <Calendar
                    v-model="form.fields[key].year"
                    view="year"
                    date-format="yy"
                    :yearNavigator="false"
                    placeholder="Год"
                    class="w-full no-year" />
                <label for="active_until">Год</label>
                <i class="pi pi-calendar calendar-icon" />
              </span>
            </span>
          </div>
          <span  v-if="getError(key) || (mainError && key === 'receipt')" class="text-xs color-error">
              {{ mainError ? mainError : getError(key) }}
          </span>
          <div v-if="value.file" class="receipt__card">
            <img src="@/assets/images/icons/file-text.svg">
            <span>{{ value.file.name }}</span>
              <button @click="destroyFileMemory(key)" :disabled="isContentUploadLoading && currentType === key" class="pi pi-times cansel-button cursor-pointer ml-auto receipt__delete" style="font-size: 1rem"></button>
          </div>
          <div style="margin-top: auto">
            <ButtonFileUpload
                v-if="!value.file"
                :index="i"
                :accept="key === 'receipt' ? '.pdf' : '.xlsx'"
                @chooseFiles="loadFileMemory"
                :isLoading="isContentUploadLoading && currentType === key"
                :label="t('receipt.button.add')"
                :multiple="false"
                :clear-files-after-select="true"
                :classes="{'btn-fix-height': true}"
            />
            <Button
                v-else
                :disabled="((key === 'receipt' || key === 'balance') && (!value.month || !value.year)) || isContentUploadLoading && currentType !== key"
                class="btn-primary btn-fix-height font-light"
                :label="t('receipt.button.upload')"
                :loading="isContentUploadLoading && currentType === key"
                @click="uploadFile(key)"
            />
          </div>
        </div>
      </MainCard>
    </div>
  </section>
  <section class="mb-3 py-2 receipt">
    <span class="receipt__title flex mb-4">{{ t('receipt.title') }}</span>
      <DataTable :value="contentData?.data?.data ? contentData.data.data : []" :loading="contentLoading" showGridlines tableStyle="min-width: 50rem" class="receipt__table">
        <Column field="uploaded_by" :header="t('receipt.table.name')" class="text-center">
          <template #body="slotProps">
            <span>{{ slotProps.data.uploaded_by.last_name }} {{ slotProps.data.uploaded_by.first_name }} {{ slotProps.data.uploaded_by.patronymic }}</span>
          </template>
        </Column>
        <Column field="uploaded_at" :header="t('receipt.table.date')" class="text-center"/>
        <Column field="type" :header="t('receipt.table.type')" class="text-center">
          <template #body="slotProps">
            <span>{{ getDataType(slotProps.data.type) }}</span>
          </template>
        </Column>
        <Column field="file" :header="t('receipt.table.file')" class="text-center">
          <template #body="slotProps">
            <a :href="slotProps.data.file" target="_blank" class="receipt__data">
              <img src="@/assets/images/icons/file-text.svg">
              <span class="color-primary">{{ getFileName(slotProps.data.file) }}</span>
            </a>
          </template>
        </Column>
        <Column field="file" :header="t('receipt.table.status')" class="text-center">
          <template #body="slotProps">
            <span class="receipt__status">
              <span :class="['receipt__status-text', slotProps.data.status]">{{ t(`receipt.status.${slotProps.data.status}`) }}</span>
            </span>
          </template>
        </Column>
        <Column field="file" header="" class="text-center">
          <template #body="slotProps">
            <button v-if="slotProps.data.status !== 'deleted'" @click="openDeleteModal(slotProps.data)" class="receipt__button">
              <img src="@/assets/images/icons/Delete.svg">
            </button>
          </template>
        </Column>
      </DataTable>
      <Paginator
          v-model:first="first"
          :rows="contentData?.data?.pagination?.per_page ?? 0"
          :totalRecords="contentData?.data?.pagination?.total ?? 0"
          class="justify-content-start"
      ></Paginator>
  </section>
</template>

<style lang="scss" scoped>
.receipt {
  &__status {
    min-width: 180px;
    &-text {
      padding: 5px 12px;
      font-size: 13px;
      font-weight: 600;
      line-height: 22px;
      text-align: center;
      color: white;
      background: #9CC5E1;
      border-radius: 30px;
      &.deleted {
        background: #E26B6B;
      }
    }
  }
  &__delete {
    border: none;
    background: transparent;
  }
  &__button {
    border: none;
    background: none;
    cursor: pointer;
    &:hover {
      opacity: 0.8;
    }
  }
  &__card {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    gap: 10px;
    min-height: 48px;
    border-radius: 4px;
    border: 1px solid var(--color-black-20);
    font-size: 13px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    word-break: break-word;
  }
  &__upload {
    display: grid;
    gap: 30px;
    grid-template-columns: repeat(1, 1fr);
    word-break: break-all;
    @media screen and (min-width: 1400px) {
      grid-template-columns: repeat(3, 1fr);
    }
    ::v-deep(.p-card-title) {
      margin-bottom: 0;
      font-size: 18px;
      font-weight: 600;
      line-height: 27px;
      text-align: left;
    }
    ::v-deep(.btn-fix-height) {
      min-width: 208px;
      font-size: 1rem;
    }
  }
  &__title {
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    text-align: left;
  }
  &__data {
    display: flex;
    gap: 10px;
    word-break: break-all;
    text-decoration: unset;
    text-align: left;
  }
  &__table {
    ::v-deep(.p-datatable-table ) {
      th {
        width: 25%;
      }
      .p-column-title {
        font-size: 18px;
        font-weight: 600;
        line-height: 21.6px;
        text-align: center;
      }
    }
  }
}
::v-deep(.p-component-overlay) {
  background-color: rgba(255, 255, 255, 0.6);
}
::v-deep(.p-datatable-loading-icon) {
  path {
    fill: var(--color-primary);
  }
}
::v-deep(.card-title) {
  word-break: break-word;
}
::v-deep(.p-card-body) {
  height: 100%;
  display: flex;
  flex-direction: column;
}
::v-deep(.p-card-content) {
  margin-top: auto;
}
.delete__title {
  word-break: break-all;
}
.receipt__upload-calendar {
  display: flex;
  flex-direction: column;
  gap: 30px;
  @media screen and (min-width: 900px) {
    flex-direction: row;
  }
  @media screen and (min-width: 1400px) {
    flex-direction: column;
  }
  @media screen and (min-width: 1920px) {
    display: grid;
    grid-template-columns: 1.3fr 1fr;
    gap: 12px;
  }
}
</style>